import React, { useState, useEffect } from 'react';
import { 
  User, 
  Mail, 
  Hash, 
  Calendar, 
  Upload,
  Save,
  Eye,
  EyeOff,
  Shield,
  ExternalLink,
  CreditCard,
  FileText,
  X,
  Download,
  Globe,
  Edit2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../components/Toast';
import axios from 'axios';
import ImageUpload from '../components/ImageUpload';
import MyWeaponCards from '../components/MyWeaponCards';
import MyWeaponMaintenance from '../components/MyWeaponMaintenance';
import MyClubs from '../components/MyClubs';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ===================== Merkeskyting-preferanser =====================
const MerkeskytingPreferencesSection = () => {
  const [optOut, setOptOut] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE}/api/merkeskyting/my-preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setOptOut(!!r.data.merkeskyting_email_optout))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = async () => {
    const newVal = !optOut;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/merkeskyting/my-preferences`,
        { merkeskyting_email_optout: newVal },
        { headers: { Authorization: `Bearer ${token}` } });
      setOptOut(newVal);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="bg-gray-800 rounded-lg p-6" data-testid="merkeskyting-preferences">
      <h3 className="text-lg font-bold text-white mb-1">Merkeskyting-varsler</h3>
      <p className="text-gray-400 text-sm mb-4">Styr hvilke automatiske e-poster du får fra Merkeskyting-modulen.</p>
      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-gray-700/40 hover:bg-gray-700/60">
        <input
          type="checkbox"
          checked={optOut}
          onChange={toggle}
          disabled={saving}
          className="mt-1 w-5 h-5 accent-yellow-500"
          data-testid="merkeskyting-email-optout"
        />
        <div className="flex-1">
          <p className="text-white font-medium">Ikke send meg diplom-e-post når jeg oppnår et nytt merke</p>
          <p className="text-gray-400 text-xs mt-1">Du kan fortsatt laste ned diplomet manuelt fra Hjem-siden. In-app varsler forblir aktive.</p>
        </div>
      </label>
    </div>
  );
};

const Profile = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { t, currentLanguage, changeLanguage, availableLanguages, loadAvailableLanguages } = useLanguage();
  const { addToast } = useToast();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  // Parse birthdate on load
  const parseBirthdate = (birth_number) => {
    if (!birth_number) return { day: '', month: '', year: '' };
    
    // Expect DD.MM.YYYY format after migration
    const match = birth_number.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (match) {
      return { day: match[1], month: match[2], year: match[3] };
    }
    return { day: '', month: '', year: '' };
  };

  const initialBirthdate = parseBirthdate(user?.birth_number);
  
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    birth_number: user?.birth_number || '', // Stored as DD.MM.YYYY
    birth_day: initialBirthdate.day,
    birth_month: initialBirthdate.month,
    birth_year: initialBirthdate.year,
    skytter_id: user?.skytter_id || user?.member_number || '' // Skytter ID
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [startKortFiles, setStartKortFiles] = useState([]);
  const [medlemsKortFiles, setMedlemsKortFiles] = useState([]);
  const [weaponCertFiles, setWeaponCertFiles] = useState([]);
  
  // Profile image states
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [showProfilePreview, setShowProfilePreview] = useState(false);

  // Password change states
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Backup state
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);

  // Email change state
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailChangePassword, setEmailChangePassword] = useState('');
  const [isChangingEmail, setIsChangingEmail] = useState(false);

  // Load existing documents when user data is available - FIXED FOR NAVIGATION
  useEffect(() => {
    if (!user) return;

    // Load available languages for this organization
    if (user.organization_id) {
      loadAvailableLanguages(user.organization_id, user.role);
    }

    // Load existing profile image - prefer S3 > Cloudinary > base64
    if (user.profile_image_url) {
      // Use the pre-computed best URL from backend (S3 > Cloudinary > base64)
      setProfileImage(user.profile_image_url);
    } else if (user.profile_image_s3_url) {
      // S3 URL (migrated to AWS)
      setProfileImage(user.profile_image_s3_url);
    } else if (user.profile_image_cloudinary_url) {
      // Cloudinary URL
      setProfileImage(user.profile_image_cloudinary_url);
    } else if (user.profile_image) {
      // Fallback to old data URL format for backward compatibility
      if (user.profile_image.startsWith('data:') || user.profile_image.startsWith('http')) {
        setProfileImage(user.profile_image);
      } else {
        setProfileImage(`data:image/jpeg;base64,${user.profile_image}`);
      }
    }
    
    console.log('📄 Profile.js useEffect triggered - loading documents from user object');
    console.log('User startkort_url:', user.startkort_url);
    console.log('User medlemskort_urls:', user.medlemskort_urls);
    console.log('User weapon_cert_urls:', user.weapon_cert_urls);
    
    // Helper to create file object from URL
    const loadSimpleFile = (url, displayName) => {
      // Guard against null/undefined URLs
      if (!url || typeof url !== 'string') {
        console.warn('⚠️ loadSimpleFile received invalid URL:', url);
        return null;
      }
      
      const fileName = url.split('/').pop();
      const extension = fileName.split('.').pop()?.toLowerCase();
      let fileType = 'application/pdf';
      
      if (extension === 'jpg' || extension === 'jpeg') {
        fileType = 'image/jpeg';
      } else if (extension === 'png') {
        fileType = 'image/png';
      }
      
      // Remove user_id prefix from filename for display
      // Format is: {user_id}_{original_filename}.ext
      // We want to show only: {original_filename}.ext
      let cleanFileName = fileName;
      if (fileName.includes('_')) {
        // Split by underscore and remove first part (user_id)
        const parts = fileName.split('_');
        if (parts.length > 1) {
          cleanFileName = parts.slice(1).join('_'); // Rejoin in case filename had underscores
        }
      }
      
      // Build full URL - use window.location.origin for /api paths (K8s ingress routing)
      // This ensures files work correctly regardless of REACT_APP_BACKEND_URL setting
      let fullUrl;
      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Already a full URL (e.g., Cloudinary URL) - use as-is
        fullUrl = url;
      } else if (url.startsWith('/api')) {
        fullUrl = `${window.location.origin}${url}`;
      } else {
        fullUrl = `${API_BASE}${url}`;
      }
      
      return {
        id: `${Date.now()}_${Math.random()}`,  // Unique ID each time to force re-render
        name: cleanFileName,
        displayName: displayName || null,  // User-defined display name
        url: fullUrl,
        serverUrl: url,
        type: fileType,
        uploaded: true,
        existing: true
      };
    };

    // CRITICAL FIX: Convert URLs to strings for comparison to detect actual changes
    const userStartkortUrl = user.startkort_url || '';
    const userMedlemskortUrls = JSON.stringify(user.medlemskort_urls || []);
    const userWeaponCertUrls = JSON.stringify(user.weapon_cert_urls || []);
    
    const currentStartkortUrl = startKortFiles[0]?.serverUrl || '';
    const currentMedlemskortUrls = JSON.stringify(medlemsKortFiles.map(f => f.serverUrl));
    const currentWeaponCertUrls = JSON.stringify(weaponCertFiles.map(f => f.serverUrl));

    // Load startkort - with displayName from backend
    if (userStartkortUrl && userStartkortUrl !== currentStartkortUrl) {
      console.log('✅ Loading startkort from backend');
      const startKortFile = loadSimpleFile(user.startkort_url, user.startkort_display_name);
      if (startKortFile) {
        setStartKortFiles([startKortFile]);
      }
    } else if (!userStartkortUrl && startKortFiles.length > 0) {
      console.log('🗑️ Clearing startkort (deleted from backend)');
      setStartKortFiles([]);
    }
    
    // Load medlemskort files - with displayNames from backend
    if (userMedlemskortUrls !== '[]' && userMedlemskortUrls !== currentMedlemskortUrls) {
      console.log('✅ Loading medlemskort from backend');
      const displayNames = user.medlemskort_display_names || [];
      const medlemskortFiles = (user.medlemskort_urls || [])
        .filter(url => url && typeof url === 'string' && url.startsWith('http'))
        .map((url, index) => loadSimpleFile(url, displayNames[index]))
        .filter(file => file !== null);
      setMedlemsKortFiles(medlemskortFiles);
    } else if (userMedlemskortUrls === '[]' && medlemsKortFiles.length > 0) {
      console.log('🗑️ Clearing medlemskort (deleted from backend)');
      setMedlemsKortFiles([]);
    }
    
    // Load weapon certificates - with displayNames from backend
    if (userWeaponCertUrls !== '[]' && userWeaponCertUrls !== currentWeaponCertUrls) {
      console.log('✅ Loading weapon certs from backend');
      const displayNames = user.weapon_cert_display_names || [];
      const weaponFiles = (user.weapon_cert_urls || [])
        .filter(url => url && typeof url === 'string' && url.startsWith('http'))
        .map((url, index) => loadSimpleFile(url, displayNames[index]))
        .filter(file => file !== null);
      setWeaponCertFiles(weaponFiles);
    } else if (userWeaponCertUrls === '[]' && weaponCertFiles.length > 0) {
      console.log('🗑️ Clearing weapon certs (deleted from backend)');
      setWeaponCertFiles([]);
    }
  }, [user]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleBirthdateChange = (e) => {
    const { name, value } = e.target;
    
    // Update the specific field
    const newFormData = {
      ...formData,
      [name]: value
    };
    
    // Build birth_number from all three fields
    if (newFormData.birth_day && newFormData.birth_month && newFormData.birth_year) {
      newFormData.birth_number = `${newFormData.birth_day}.${newFormData.birth_month}.${newFormData.birth_year}`;
    } else {
      newFormData.birth_number = '';
    }
    
    setFormData(newFormData);
  };

  const handleSaveDocuments = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      // Prepare document URLs for saving
      // CRITICAL: Extract serverUrl from CURRENT state (which includes deletions and additions)
      const documentData = {
        startkort_url: startKortFiles.length > 0 && startKortFiles[0].serverUrl ? startKortFiles[0].serverUrl : null,
        medlemskort_urls: medlemsKortFiles.filter(f => f.serverUrl && f.uploaded).map(f => f.serverUrl),
        weapon_cert_urls: weaponCertFiles.filter(f => f.serverUrl && f.uploaded).map(f => f.serverUrl)
      };

      console.log('💾 Saving documents to backend:', documentData);

      // Update user profile with document URLs
      const response = await axios.put(`${API_BASE}/api/users/${user.id}`, documentData, { headers });
      
      console.log('✅ Documents saved successfully to backend');
      console.log('Backend response:', response.data);

      // CRITICAL: Force refresh user data from backend to sync state
      // Use refreshUser() instead of updateUser() to fetch FRESH data from /api/auth/me
      const refreshSuccess = await refreshUser();
      
      if (refreshSuccess) {
        console.log('✅ User data refreshed from backend - new files should now appear');
      } else {
        console.warn('⚠️ Failed to refresh user data - you may need to reload the page');
      }

      setSuccess('Dokumenter lagret!');
      addToast('Dokumenter lagret permanent på profilen din', 'success');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('❌ Error saving documents:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || 'Kunne ikke lagre dokumenter');
      addToast('Kunne ikke lagre dokumenter', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.put(`${API_BASE}/api/users/${user.id}`, formData);
      
      // Vis elegant toast melding
      addToast('Profil oppdatert!', 'success');
      setEditing(false);
      
      // Refresh user context etter litt delay så toast kan vises
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      addToast(error.response?.data?.detail || 'Kunne ikke oppdatere profil', 'error');
      setError(error.response?.data?.detail || 'Kunne ikke oppdatere profil');
    } finally {
      setLoading(false);
    }
  };

  // Profile image upload functionality
  const handleProfileImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      addToast('Kun bildefiler er tillatt', 'error');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      const fileSizeMB = (file.size / 1024 / 1024).toFixed(1);
      addToast(
        `Bildet er for stort (${fileSizeMB}MB). Maksimal størrelse er 10MB. ` +
        `Tips: Reduser bildestørrelsen på telefonen eller komprimer bildet før opplasting.`,
        'error',
        8000
      );
      return;
    }

    setUploadingProfileImage(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result;
          
          const response = await axios.post(
            `${API_BASE}/api/users/profile-image/upload`,
            { image_base64: base64Data },
            {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          if (response.data.success) {
            // Use S3 URL from response
            const newImageUrl = response.data.profile_image_s3_url || response.data.image_url;
            setProfileImage(newImageUrl);
            addToast('Profilbilde opplastet til S3!', 'success');
            
            // Update user context with S3 URL
            if (updateUser) {
              updateUser({
                ...user, 
                profile_image_s3_url: newImageUrl,
                profile_image_url: newImageUrl
              });
            }
          }
        } catch (error) {
          console.error('Profile image upload error:', error);
          addToast('Kunne ikke laste opp profilbilde', 'error');
        } finally {
          setUploadingProfileImage(false);
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File processing error:', error);
      addToast('Kunne ikke behandle bildefilen', 'error');
      setUploadingProfileImage(false);
    }
  };

  const handleProfileImageDelete = async () => {
    if (!window.confirm('Er du sikker på at du vil slette profilbildet?')) return;

    setUploadingProfileImage(true);

    try {
      const response = await axios.delete(
        `${API_BASE}/api/users/me/profile-image`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setProfileImage(null);
        addToast('Profilbilde slettet', 'success');
        
        // Update user context - clear ALL profile image fields
        if (updateUser) {
          updateUser({
            ...user, 
            profile_image: null,
            profile_image_s3_url: null,
            profile_image_cloudinary_url: null,
            profile_image_url: null
          });
        }
      }
    } catch (error) {
      console.error('Profile image delete error:', error);
      addToast('Kunne ikke slette profilbilde', 'error');
    } finally {
      setUploadingProfileImage(false);
    }
  };

  // Password change functionality
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addToast('Nye passord stemmer ikke overens', 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      addToast('Nytt passord må være minst 6 tegn langt', 'error');
      return;
    }

    setPasswordLoading(true);

    try {
      await axios.put(`${API_BASE}/api/users/${user.id}/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      addToast('Passord endret vellykket', 'success');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Password change error:', error);
      if (error.response?.data?.detail) {
        addToast(error.response.data.detail, 'error');
      } else {
        addToast('Kunne ikke endre passord. Sjekk at nåværende passord er korrekt.', 'error');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePasswordInputChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field]
    });
  };

  // Download own data backup as ZIP file
  const downloadMyBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/profile/backup`, { 
        headers,
        responseType: 'blob'  // Important: receive as blob for ZIP file
      });
      
      // Create download link for ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'min_backup.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      addToast('Din data-backup (ZIP-fil) er lastet ned!', 'success');
    } catch (error) {
      console.error('Failed to create backup:', error);
      addToast('Kunne ikke laste ned backup. Prøv igjen.', 'error');
    } finally {
      setIsCreatingBackup(false);
    }
  };

  // Change own email address
  const handleEmailChange = async () => {
    if (!newEmail.trim()) {
      addToast('Vennligst skriv inn ny e-postadresse', 'error');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      addToast('Ugyldig e-postformat', 'error');
      return;
    }

    if (!emailChangePassword) {
      addToast('Vennligst skriv inn ditt nåværende passord for å bekrefte', 'error');
      return;
    }

    setIsChangingEmail(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.put(
        `${API_BASE}/api/profile/email`,
        {
          new_email: newEmail.trim().toLowerCase(),
          current_password: emailChangePassword
        },
        { headers }
      );

      if (response.data.success) {
        addToast('E-postadresse oppdatert! Sjekk begge e-postadressene for bekreftelse.', 'success');
        setShowEmailChangeModal(false);
        setNewEmail('');
        setEmailChangePassword('');
        // Refresh user data
        await refreshUser();
      }
    } catch (error) {
      console.error('Failed to change email:', error);
      const errorMessage = error?.response?.data?.detail || 'Kunne ikke endre e-post. Sjekk passordet og prøv igjen.';
      addToast(errorMessage, 'error');
    } finally {
      setIsChangingEmail(false);
    }
  };

  // File upload functionality now handled by ImageUpload component

  const getUserRoleDisplay = (role) => {
    const roles = {
      'super_admin': 'Super Administrator',
      'org_admin': 'Organisasjon Administrator',
      'range_officer': 'Skytebane Officer',
      'member': 'Medlem'
    };
    return roles[role] || role;
  };

  const getUserRoleColor = (role) => {
    const colors = {
      'super_admin': 'text-purple-400 bg-purple-500/20',
      'org_admin': 'text-blue-400 bg-blue-500/20',
      'range_officer': 'text-yellow-400 bg-yellow-500/20',
      'member': 'text-green-400 bg-green-500/20'
    };
    return colors[role] || 'text-gray-400 bg-gray-500/20';
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold text-white">{t('profile.title', 'Min Profil')}</h1>
        <p className="text-gray-400 mt-1">
          Administrer din profil og kontoinformasjon
        </p>
      </div>

      {/* MULTI-CLUB: Mine klubber - vises kun for medlemmer i flere klubber */}
      <MyClubs />

      {/* Profile Card */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-start sm:items-center flex-col sm:flex-row gap-4 sm:gap-6 mb-6">
          {/* Profile Image */}
          <div className="relative flex-shrink-0">
            <div className="h-20 w-20 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-600">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profilbilde"
                  className="h-20 w-20 rounded-full object-cover cursor-pointer"
                  onClick={() => setShowProfilePreview(true)}
                />
              ) : (
                <User className="h-10 w-10 text-white" />
              )}
            </div>
            
            {/* Profile Image Upload Buttons */}
            <div className="absolute -bottom-2 -right-2 flex space-x-1">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleProfileImageUpload(e.target.files[0])}
                  disabled={uploadingProfileImage}
                />
                <div className="bg-blue-500 hover:bg-blue-400 text-white p-1 rounded-full shadow-lg transition-colors">
                  <Upload className="h-3 w-3" />
                </div>
              </label>
              
              {profileImage && (
                <button
                  onClick={handleProfileImageDelete}
                  disabled={uploadingProfileImage}
                  className="bg-red-500 hover:bg-red-400 text-white p-1 rounded-full shadow-lg transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            
            {uploadingProfileImage && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-white truncate">{user.full_name}</h2>
                <p className="text-gray-400 text-sm truncate">{user.email}</p>
              </div>
              {/* Edit Button - Icon only on mobile for space saving */}
              <button
                onClick={() => {
                  setEditing(!editing);
                  setFormData({
                    full_name: user.full_name || '',
                    birth_number: user.birth_number || '',
                    skytter_id: user.skytter_id || user.member_number || ''
                  });
                  setError('');
                  setSuccess('');
                }}
                className="flex-shrink-0 px-3 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors flex items-center gap-1.5"
                title={editing ? 'Avbryt redigering' : 'Rediger profil'}
              >
                {editing ? (
                  <>
                    <X className="h-4 w-4" />
                    <span className="hidden sm:inline">Avbryt</span>
                  </>
                ) : (
                  <>
                    <Edit2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Rediger</span>
                  </>
                )}
              </button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {/* Always show member role if user has admin rights */}
              {(user.role === 'org_admin' || user.role === 'range_officer') && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-green-400 bg-green-500/20">
                  <Shield className="h-3 w-3 mr-1" />
                  Medlem
                </span>
              )}
              
              {/* Always show the primary role */}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserRoleColor(user.role)}`}>
                <Shield className="h-3 w-3 mr-1" />
                {getUserRoleDisplay(user.role)}
              </span>
            </div>
          </div>
        </div>

        {/* Profile Form */}
        {editing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Fullt navn
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Fornavn Etternavn"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Fødselsdato *
              </label>
              <div className="flex gap-2">
                {/* Day dropdown */}
                <div className="flex-1">
                  <select
                    name="birth_day"
                    value={formData.birth_day || ''}
                    onChange={handleBirthdateChange}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Dag</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day.toString().padStart(2, '0')}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Month dropdown */}
                <div className="flex-1">
                  <select
                    name="birth_month"
                    value={formData.birth_month || ''}
                    onChange={handleBirthdateChange}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">Måned</option>
                    <option value="01">Januar</option>
                    <option value="02">Februar</option>
                    <option value="03">Mars</option>
                    <option value="04">April</option>
                    <option value="05">Mai</option>
                    <option value="06">Juni</option>
                    <option value="07">Juli</option>
                    <option value="08">August</option>
                    <option value="09">September</option>
                    <option value="10">Oktober</option>
                    <option value="11">November</option>
                    <option value="12">Desember</option>
                  </select>
                </div>
                
                {/* Year dropdown */}
                <div className="flex-1">
                  <select
                    name="birth_year"
                    value={formData.birth_year || ''}
                    onChange={handleBirthdateChange}
                    className="w-full px-3 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="">År</option>
                    {Array.from({ length: 105 }, (_, i) => 2024 - i).map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                * Brukes til PDF-eksport for våpensøknader til politiet
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Skytter ID
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  name="skytter_id"
                  value={formData.skytter_id}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Din Skytter ID"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">
                <a 
                  href="https://app.skyting.no/user" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400 inline-flex items-center"
                >
                  Finn din Skytter ID her <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </p>
            </div>

            {error && (
              <div className="bg-red-600/20 border border-red-600/50 rounded-md p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-600/20 border border-green-600/50 rounded-md p-4">
                <p className="text-green-400 text-sm">{success}</p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 transition-colors disabled:opacity-50"
              >
                <Save className="h-5 w-5 mr-2" />
                {loading ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                E-post adresse
              </label>
              <div className="flex items-center space-x-2 text-gray-400">
                <Mail className="h-4 w-4" />
                <span>{user.email}</span>
                <button
                  onClick={() => {
                    setNewEmail('');
                    setEmailChangePassword('');
                    setShowEmailChangeModal(true);
                  }}
                  className="ml-2 text-yellow-500 hover:text-yellow-400 text-sm font-medium"
                >
                  Endre
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Fødselsdato <span className="text-yellow-500 text-xs font-normal">(Påkrevd for våpensøknad)</span>
              </label>
              <div className="flex items-center space-x-2 text-gray-400">
                <Calendar className="h-4 w-4" />
                <span>{user.birth_number || 'Ikke registrert'}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Skytter ID
              </label>
              <div className="flex items-center space-x-2 text-gray-400">
                <Hash className="h-4 w-4" />
                <span>{user.skytter_id || user.member_number || 'Ikke satt'}</span>
                <a 
                  href="https://app.skyting.no/user" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400 ml-2"
                  title="Finn din Skytter ID"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Kontostatus
              </label>
              <div className="flex items-center space-x-2">
                <div className={`h-2 w-2 rounded-full ${
                  user.active && user.approved ? 'bg-green-500' : 'bg-orange-500'
                }`}></div>
                <span className="text-gray-400">
                  {user.active && user.approved ? 'Aktiv' : 'Venter godkjenning'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Organization Info */}
      {user.organization && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-bold text-white mb-4">Organisasjonsinformasjon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-400">Organisasjon</p>
              <p className="text-white font-medium">{user.organization.name}</p>
            </div>
            {user.organization.email && (
              <div>
                <p className="text-sm text-gray-400">Kontakt</p>
                <p className="text-white font-medium">{user.organization.email}</p>
              </div>
            )}
            {user.organization.website && (
              <div>
                <p className="text-sm text-gray-400">Nettside</p>
                <a
                  href={user.organization.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-500 hover:text-yellow-400"
                >
                  {user.organization.website}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mine Våpenkort Section */}
      <MyWeaponCards />

      {/* Våpenpuss egne våpen Section */}
      <MyWeaponMaintenance />

      {/* Security Info */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4">Sikkerhet</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Passord</p>
              <p className="text-sm text-gray-400">Sist endret: Ukjent</p>
            </div>
            <button 
              onClick={() => setShowPasswordModal(true)}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors">
              Endre passord
            </button>
          </div>
          
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-center space-x-2 text-sm">
              <div className="h-2 w-2 bg-green-500 rounded-full"></div>
              <span className="text-gray-400">Konto er sikret og aktiv</span>
            </div>
          </div>
        </div>
      </div>

      {/* Data Backup Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <Download className="w-5 h-5 mr-2" />
          Min databackup
        </h3>
        <p className="text-gray-400 text-sm mb-4">
          Last ned en komplett kopi av alle dine data fra Aktivlogg. 
        </p>
        <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-4 mb-4">
          <p className="text-blue-300 text-sm mb-2">
            <strong>ZIP-filen inneholder:</strong>
          </p>
          <ul className="text-blue-400/80 text-sm space-y-1 list-disc list-inside">
            <li>Excel-fil med treningsøkter, våpenkort, dugnad og profil</li>
            <li>Alle opplastede filer (profilbilde, QR-koder, dokumenter)</li>
            <li>JSON-fil for re-import ved bytte av klubb</li>
          </ul>
        </div>
        <button 
          onClick={downloadMyBackup}
          disabled={isCreatingBackup}
          className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {isCreatingBackup ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-gray-900 border-t-transparent rounded-full"></div>
              Oppretter backup...
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Last ned min data (ZIP)
            </>
          )}
        </button>
      </div>

      {/* Personal Language Settings - DISABLED */}
      {false && <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <Globe className="w-5 h-5 mr-2" />
          {t('profile.languageSettings', 'Språkinnstillinger')}
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {t('profile.preferredLanguage', 'Foretrukket språk')}
            </label>
            <select
              value={currentLanguage}
              onChange={async (e) => {
                const newLanguage = e.target.value;
                try {
                  await changeLanguage(newLanguage, user?.organization_id, user?.role);
                  addToast(`${t('profile.languageChanged', 'Språk endret til')} ${availableLanguages.find(lang => lang.code === newLanguage)?.name || newLanguage}`, 'success');
                } catch (error) {
                  addToast(t('profile.languageError', 'Kunne ikke endre språk'), 'error');
                }
              }}
              className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.name}
                </option>
              ))}
            </select>
            <p className="text-gray-400 text-sm mt-2">
              {t('profile.languageDescription', 'Dette endrer språket kun for din brukeropplevelse. Standard språk er norsk bokmål.')}
            </p>
          </div>
        </div>
      </div>}

      {/* Documents Section */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-bold text-white mb-4">Dokumenter</h3>
        <div className="space-y-6">
          
          {/* Startkort Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-white">Startkort og Medlemskort</h4>
              <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded">Valgfritt</span>
            </div>
            
            <ImageUpload
              type="startkort"
              files={startKortFiles}
              onFilesChange={setStartKortFiles}
              multiple={false}
              title="Last opp ditt Startkort"
              description="PDF, JPG, PNG • Auto-komprimering over 3MB"
              infoLink={{
                url: "https://app.skyting.no/",
                text: "Her finner du startkortet"
              }}
            />

            <ImageUpload
              type="medlemskort"
              files={medlemsKortFiles}
              onFilesChange={setMedlemsKortFiles}
              multiple={true}
              title="Last opp ditt Medlemskort"
              description="PDF, JPG, PNG • Auto-komprimering over 3MB • Flere filer tillatt"
              infoLink={{
                url: "https://www.minidrett.no/profil/medlemskap",
                text: "Her finner du medlemskortet"
              }}
            />
          </div>

          {/* Other Documents */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-md font-semibold text-white">Andre filer (Våpenskap, Diplom Sikkerhetskurs ++)</h4>
              <span className="text-xs text-green-400 bg-green-400/20 px-2 py-1 rounded">Valgfritt</span>
            </div>
            
            <ImageUpload
              type="weapon_cert"
              files={weaponCertFiles}
              onFilesChange={setWeaponCertFiles}
              multiple={true}
              title="Våpensertifikat / Diplom"
              description="PDF, JPG, PNG • Auto-komprimering over 3MB • Flere filer tillatt"
            />
          </div>

          {/* Save Documents Button */}
          <div className="flex justify-end pt-4 border-t border-gray-700">
            <button
              onClick={handleSaveDocuments}
              disabled={loading}
              className="flex items-center px-6 py-3 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-5 w-5 mr-2" />
              {loading ? 'Lagrer...' : 'Lagre dokumenter'}
            </button>
          </div>
        </div>
      </div>

      {/* Merkeskyting-preferanser */}
      <MerkeskytingPreferencesSection />


      {/* Profile Image Preview Modal */}
      {showProfilePreview && profileImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="relative max-w-md mx-4">
            <button
              onClick={() => setShowProfilePreview(false)}
              className="absolute -top-10 -right-10 text-white hover:text-gray-300 text-2xl font-bold"
            >
              ×
            </button>
            <img
              src={profileImage}
              alt="Profilbilde forhåndsvisning"
              className="max-w-full max-h-96 rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center">
              <p className="text-white text-sm">Profilbilde (200x200px, 72dpi)</p>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">Endre passord</h2>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nåværende passord
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordInputChange}
                    required
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('current')}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                  >
                    {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nytt passord
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordInputChange}
                    required
                    minLength={6}
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('new')}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                  >
                    {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minst 6 tegn</p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bekreft nytt passord
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordInputChange}
                    required
                    className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 pr-10 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => togglePasswordVisibility('confirm')}
                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-white"
                  >
                    {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? 'Endrer...' : 'Endre passord'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Change Modal */}
      {showEmailChangeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-600 p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-yellow-500/20 p-2 rounded-full">
                  <Mail className="h-6 w-6 text-yellow-500" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  Endre e-postadresse
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setNewEmail('');
                  setEmailChangePassword('');
                }}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Current Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Nåværende e-postadresse
              </label>
              <div className="px-4 py-3 bg-gray-700/50 rounded-lg text-gray-300 border border-gray-600">
                {user.email}
              </div>
            </div>

            {/* New Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Ny e-postadresse <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="ny.epost@eksempel.no"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
            </div>

            {/* Password Confirmation */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-400 mb-2">
                Bekreft med passord <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                value={emailChangePassword}
                onChange={(e) => setEmailChangePassword(e.target.value)}
                placeholder="Ditt nåværende passord"
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
              />
              <p className="text-gray-500 text-sm mt-2">
                For din sikkerhet må du bekrefte endringen med passordet ditt.
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3 mb-6">
              <p className="text-blue-300 text-sm">
                Du vil motta en bekreftelse på både gammel og ny e-postadresse.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowEmailChangeModal(false);
                  setNewEmail('');
                  setEmailChangePassword('');
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600"
              >
                Avbryt
              </button>
              <button
                onClick={handleEmailChange}
                disabled={!newEmail.trim() || !emailChangePassword || isChangingEmail}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isChangingEmail ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-gray-900 border-t-transparent rounded-full"></div>
                    Endrer...
                  </>
                ) : (
                  'Endre e-post'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;